import {
  getObject,
  getLeoUuid,
  LeoUUID,
  getInt32,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { BoundedName, BoundedNameDTO } from "../types/boundedName";
import { KeycloakEmail, KeycloakEmailDTO } from "../types/keycloakEmail";
import { EmailContent, EmailContentDTO } from "./emailContent";
import { ScheduleType } from "./scheduleType";
import { RespondentSelection } from "../types/respondentSelection";
/* eslint-disable import/export */
export abstract class CreateEmailCycleRPC implements
  LeoRPC<CreateEmailCycleRPC.Request, CreateEmailCycleRPC.Response, CreateEmailCycleRPC.Errors.Errors>
{
  abstract execute(
    request: CreateEmailCycleRPC.Request
  ): Promise<LeoRPCResult<CreateEmailCycleRPC.Response, CreateEmailCycleRPC.Errors.Errors>>;
}

export namespace CreateEmailCycleRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    cycleName: BoundedNameDTO;
    projectId: string;
    senderEmail: KeycloakEmailDTO;
    senderName: BoundedNameDTO;
    emailContent: EmailContentDTO;
    scheduleType: ScheduleType.ScheduleTypeDTO;
    respondents: RespondentSelection.RespondentSelectionDTO;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isCreateEmailCycleRPCRequest: boolean = true;
  
    readonly cycleName: BoundedName;
    readonly projectId: LeoUUID;
    readonly senderEmail: KeycloakEmail;
    readonly senderName: BoundedName;
    readonly emailContent: EmailContent;
    readonly scheduleType: ScheduleType.ScheduleType;
    readonly respondents: RespondentSelection.RespondentSelection;
  
  
    constructor(
    cycleName: BoundedName, 
    projectId: LeoUUID, 
    senderEmail: KeycloakEmail, 
    senderName: BoundedName, 
    emailContent: EmailContent, 
    scheduleType: ScheduleType.ScheduleType, 
    respondents: RespondentSelection.RespondentSelection
    ) {
      this.cycleName = cycleName;
      this.projectId = projectId;
      this.senderEmail = senderEmail;
      this.senderName = senderName;
      this.emailContent = emailContent;
      this.scheduleType = scheduleType;
      this.respondents = respondents;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const cycleName = BoundedName.fromDTO(getObject(dto, "cycleName", InvalidRequestCycleNameError));
      const projectId = getLeoUuid(dto, "projectId", InvalidRequestProjectIdError);
      const senderEmail = KeycloakEmail.fromDTO(getObject(dto, "senderEmail", InvalidRequestSenderEmailError));
      const senderName = BoundedName.fromDTO(getObject(dto, "senderName", InvalidRequestSenderNameError));
      const emailContent = EmailContent.fromDTO(getObject(dto, "emailContent", InvalidRequestEmailContentError));
      const scheduleType = ScheduleType.fromDTO(getObject(dto, "scheduleType", InvalidRequestScheduleTypeError));
      const respondents = RespondentSelection.fromDTO(getObject(dto, "respondents", InvalidRequestRespondentsError));
      return new Request(
        cycleName,
        projectId,
        senderEmail,
        senderName,
        emailContent,
        scheduleType,
        respondents
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          cycleName: this.cycleName.toDTO(),
          projectId: this.projectId.toDTO(),
          senderEmail: this.senderEmail.toDTO(),
          senderName: this.senderName.toDTO(),
          emailContent: this.emailContent.toDTO(),
          scheduleType: this.scheduleType.toDTO(),
          respondents: this.respondents.toDTO()
      };
      return dto;
    }
    }
  
  Request.prototype.toString = function toString(): string {
    return `CreateEmailCycleRPC(cycleName='${this.cycleName}',projectId='${this.projectId}',senderEmail='${this.senderEmail}',senderName='${this.senderName}',emailContent='${this.emailContent}',scheduleType='${this.scheduleType}',respondents='${this.respondents}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {}

  export class Response implements LeoRPCResponse {
    static fromDTO(): Response {
      return new Response();
    }

    toDTO(): ResponseDTO {
      return {}
    }
  }

  Response.prototype.toString = function toString(): string {
    return `CreateEmailCycleRPC.Response()`;
  };

  export enum RPCError {
    InvalidProjectId = "INVALID_PROJECT_ID",
    ProjectAlreadyArchived = "PROJECT_ALREADY_ARCHIVED",
    InvalidSenderEmail = "INVALID_SENDER_EMAIL",
    DomainNotAllowed = "DOMAIN_NOT_ALLOWED",
    DuplicateCycleName = "DUPLICATE_CYCLE_NAME",
    InvalidSchedule = "INVALID_SCHEDULE",
    PendingSurveyAssignment = "PENDING_SURVEY_ASSIGNMENT",
    MissingEmailId = "MISSING_EMAIL_ID"
  }

  export namespace Errors {
    
    export interface InvalidProjectIdDTO {
      code: string;
    }
    
    export class InvalidProjectId extends LeoRPCError<RPCError> {
      readonly isInvalidProjectId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidProjectId;
        super(code);
      }
    
    
      toDTO(): InvalidProjectIdDTO {
        const dto: InvalidProjectIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidProjectId.prototype.toString = function toString(): string {
      return `CreateEmailCycleRPC.INVALID_PROJECT_ID()`;
    };
    
    
    export interface ProjectAlreadyArchivedDTO {
      code: string;
    }
    
    export class ProjectAlreadyArchived extends LeoRPCError<RPCError> {
      readonly isProjectAlreadyArchived: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.ProjectAlreadyArchived;
        super(code);
      }
    
    
      toDTO(): ProjectAlreadyArchivedDTO {
        const dto: ProjectAlreadyArchivedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    ProjectAlreadyArchived.prototype.toString = function toString(): string {
      return `CreateEmailCycleRPC.PROJECT_ALREADY_ARCHIVED()`;
    };
    
    
    export interface InvalidSenderEmailDTO {
      code: string;
    }
    
    export class InvalidSenderEmail extends LeoRPCError<RPCError> {
      readonly isInvalidSenderEmail: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidSenderEmail;
        super(code);
      }
    
    
      toDTO(): InvalidSenderEmailDTO {
        const dto: InvalidSenderEmailDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidSenderEmail.prototype.toString = function toString(): string {
      return `CreateEmailCycleRPC.INVALID_SENDER_EMAIL()`;
    };
    
    
    export interface DomainNotAllowedDTO {
      code: string;
    }
    
    export class DomainNotAllowed extends LeoRPCError<RPCError> {
      readonly isDomainNotAllowed: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.DomainNotAllowed;
        super(code);
      }
    
    
      toDTO(): DomainNotAllowedDTO {
        const dto: DomainNotAllowedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    DomainNotAllowed.prototype.toString = function toString(): string {
      return `CreateEmailCycleRPC.DOMAIN_NOT_ALLOWED()`;
    };
    
    
    export interface DuplicateCycleNameDTO {
      code: string;
    }
    
    export class DuplicateCycleName extends LeoRPCError<RPCError> {
      readonly isDuplicateCycleName: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.DuplicateCycleName;
        super(code);
      }
    
    
      toDTO(): DuplicateCycleNameDTO {
        const dto: DuplicateCycleNameDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    DuplicateCycleName.prototype.toString = function toString(): string {
      return `CreateEmailCycleRPC.DUPLICATE_CYCLE_NAME()`;
    };
    
    
    export interface InvalidScheduleDTO {
      code: string;
    }
    
    export class InvalidSchedule extends LeoRPCError<RPCError> {
      readonly isInvalidSchedule: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidSchedule;
        super(code);
      }
    
    
      toDTO(): InvalidScheduleDTO {
        const dto: InvalidScheduleDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidSchedule.prototype.toString = function toString(): string {
      return `CreateEmailCycleRPC.INVALID_SCHEDULE()`;
    };
    
    
    export interface PendingSurveyAssignmentDTO {
      code: string;
      respondentsSelected: number;
      respondentsAssigned: number;
      respondentsUnassigned: number;
    }
    
    export class PendingSurveyAssignment extends LeoRPCError<RPCError> {
      readonly isPendingSurveyAssignment: boolean = true;
    
      readonly respondentsSelected: number;
      readonly respondentsAssigned: number;
      readonly respondentsUnassigned: number;
    
      constructor(
      respondentsSelected: number, 
      respondentsAssigned: number, 
      respondentsUnassigned: number
      ) {
        const code = RPCError.PendingSurveyAssignment;
        super(code);
        this.respondentsSelected = respondentsSelected;
        this.respondentsAssigned = respondentsAssigned;
        this.respondentsUnassigned = respondentsUnassigned;
      }
    
      static fromDTO(dto: PendingSurveyAssignmentDTO): PendingSurveyAssignment {
        const respondentsSelected = getInt32(dto, "respondentsSelected", InvalidResponsePendingSurveyAssignmentRespondentsSelectedError);
        const respondentsAssigned = getInt32(dto, "respondentsAssigned", InvalidResponsePendingSurveyAssignmentRespondentsAssignedError);
        const respondentsUnassigned = getInt32(dto, "respondentsUnassigned", InvalidResponsePendingSurveyAssignmentRespondentsUnassignedError);
        return new PendingSurveyAssignment(
          respondentsSelected,
          respondentsAssigned,
          respondentsUnassigned
        );
      }
    
      toDTO(): PendingSurveyAssignmentDTO {
        const dto: PendingSurveyAssignmentDTO = {
          code: this.code,
          respondentsSelected: this.respondentsSelected,
          respondentsAssigned: this.respondentsAssigned,
          respondentsUnassigned: this.respondentsUnassigned
        };
        return dto;
      }
    
      }
    
    PendingSurveyAssignment.prototype.toString = function toString(): string {
      return `CreateEmailCycleRPC.PENDING_SURVEY_ASSIGNMENT(respondentsSelected='${this.respondentsSelected}',respondentsAssigned='${this.respondentsAssigned}',respondentsUnassigned='${this.respondentsUnassigned}')`;
    };
    
    
    export interface MissingEmailIdDTO {
      code: string;
      count: number;
    }
    
    export class MissingEmailId extends LeoRPCError<RPCError> {
      readonly isMissingEmailId: boolean = true;
    
      readonly count: number;
    
      constructor(
      count: number
      ) {
        const code = RPCError.MissingEmailId;
        super(code);
        this.count = count;
      }
    
      static fromDTO(dto: MissingEmailIdDTO): MissingEmailId {
        const count = getInt32(dto, "count", InvalidResponseMissingEmailIdCountError);
        return new MissingEmailId(
          count
        );
      }
    
      toDTO(): MissingEmailIdDTO {
        const dto: MissingEmailIdDTO = {
          code: this.code,
          count: this.count
        };
        return dto;
      }
    
      }
    
    MissingEmailId.prototype.toString = function toString(): string {
      return `CreateEmailCycleRPC.MISSING_EMAIL_ID(count='${this.count}')`;
    };
    

    export type Errors = InvalidProjectId | ProjectAlreadyArchived | InvalidSenderEmail | DomainNotAllowed | DuplicateCycleName | InvalidSchedule | PendingSurveyAssignment | MissingEmailId;
    export type ErrorsDTO = InvalidProjectIdDTO | ProjectAlreadyArchivedDTO | InvalidSenderEmailDTO | DomainNotAllowedDTO | DuplicateCycleNameDTO | InvalidScheduleDTO | PendingSurveyAssignmentDTO | MissingEmailIdDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidProjectId:
          return new InvalidProjectId();
        case RPCError.ProjectAlreadyArchived:
          return new ProjectAlreadyArchived();
        case RPCError.InvalidSenderEmail:
          return new InvalidSenderEmail();
        case RPCError.DomainNotAllowed:
          return new DomainNotAllowed();
        case RPCError.DuplicateCycleName:
          return new DuplicateCycleName();
        case RPCError.InvalidSchedule:
          return new InvalidSchedule();
        case RPCError.PendingSurveyAssignment:
          return PendingSurveyAssignment.fromDTO(dto as PendingSurveyAssignmentDTO);
        case RPCError.MissingEmailId:
          return MissingEmailId.fromDTO(dto as MissingEmailIdDTO);
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestCycleNameError extends CreateEmailCycleRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestCycleNameError";
  }
}

export class InvalidRequestProjectIdError extends CreateEmailCycleRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestProjectIdError";
  }
}

export class InvalidRequestSenderEmailError extends CreateEmailCycleRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSenderEmailError";
  }
}

export class InvalidRequestSenderNameError extends CreateEmailCycleRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSenderNameError";
  }
}

export class InvalidRequestEmailContentError extends CreateEmailCycleRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestEmailContentError";
  }
}

export class InvalidRequestScheduleTypeError extends CreateEmailCycleRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestScheduleTypeError";
  }
}

export class InvalidRequestRespondentsError extends CreateEmailCycleRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestRespondentsError";
  }
}


export class InvalidResponseInvalidProjectIdError extends CreateEmailCycleRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidProjectIdError";
  }
}

export class InvalidResponseProjectAlreadyArchivedError extends CreateEmailCycleRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseProjectAlreadyArchivedError";
  }
}

export class InvalidResponseInvalidSenderEmailError extends CreateEmailCycleRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidSenderEmailError";
  }
}

export class InvalidResponseDomainNotAllowedError extends CreateEmailCycleRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseDomainNotAllowedError";
  }
}

export class InvalidResponseDuplicateCycleNameError extends CreateEmailCycleRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseDuplicateCycleNameError";
  }
}

export class InvalidResponseInvalidScheduleError extends CreateEmailCycleRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidScheduleError";
  }
}

export class InvalidResponsePendingSurveyAssignmentError extends CreateEmailCycleRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponsePendingSurveyAssignmentError";
  }
}

export class InvalidResponsePendingSurveyAssignmentRespondentsSelectedError extends InvalidResponsePendingSurveyAssignmentError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponsePendingSurveyAssignmentRespondentsSelectedError";
  }
}

export class InvalidResponsePendingSurveyAssignmentRespondentsAssignedError extends InvalidResponsePendingSurveyAssignmentError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponsePendingSurveyAssignmentRespondentsAssignedError";
  }
}

export class InvalidResponsePendingSurveyAssignmentRespondentsUnassignedError extends InvalidResponsePendingSurveyAssignmentError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponsePendingSurveyAssignmentRespondentsUnassignedError";
  }
}
export class InvalidResponseMissingEmailIdError extends CreateEmailCycleRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseMissingEmailIdError";
  }
}

export class InvalidResponseMissingEmailIdCountError extends InvalidResponseMissingEmailIdError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseMissingEmailIdCountError";
  }
}
